import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Search, Calendar, Loader2, ArrowRight, Building2, Receipt, CreditCard, Menu, ShieldCheck } from 'lucide-react';
import { api } from '../services/api';
import { useDateRange, RANGE_PRESETS } from '../context/DateRangeContext';

/** Enough characters that the first keystroke doesn't ask the server for every
 *  owner on the platform. */
const MIN_QUERY = 2;

const TopBar = ({ userRole, onMenuClick }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dateRange = useDateRange();

    const [term, setTerm] = useState('');
    const [query, setQuery] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [searchError, setSearchError] = useState('');
    const [open, setOpen] = useState(false);
    const containerRef = useRef(null);

    const isSuperAdmin = userRole === 'super_admin';
    const routeTitle = {
        '/': ['Overview', 'Platform health and owner operations'],
        '/library': ['Library owners', 'Accounts, plans and library health'],
        '/reports': ['Reports', 'Platform performance and trends'],
        '/payments': ['Plans & coupons', 'Pricing, promotions and revenue'],
        '/payment-receipts': ['Payment receipts', 'Verified owner transactions'],
        '/team': ['Team', 'Internal access and roles'],
        '/activity': ['Activity log', 'Audited platform actions'],
        '/delete-requests': ['Deletion requests', 'Privacy operations queue'],
        '/settings': ['Platform settings', 'Company and support configuration'],
    }[location.pathname] || ['LibraryDesk', 'Platform operations'];

    useEffect(() => {
        const timer = setTimeout(() => setQuery(term.trim()), 350);
        return () => clearTimeout(timer);
    }, [term]);

    useEffect(() => {
        if (query.length < MIN_QUERY) {
            setResults([]);
            setSearchError('');
            setSearching(false);
            return;
        }

        let cancelled = false;
        const run = async () => {
            setSearching(true);
            setSearchError('');
            try {
                const response = await api.get(`/admin/users?search=${encodeURIComponent(query)}&limit=8`);
                if (cancelled) return;
                setResults(response.users || []);
            } catch (err) {
                if (cancelled) return;
                setResults([]);
                setSearchError(err.message || 'Search failed');
            } finally {
                if (!cancelled) setSearching(false);
            }
        };

        run();
        return () => { cancelled = true; };
    }, [query]);

    // Closing on blur races the click that picks a result, so the outside
    // click is watched on the document instead.
    useEffect(() => {
        const onPointerDown = (event) => {
            if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onPointerDown);
        return () => document.removeEventListener('mousedown', onPointerDown);
    }, []);

    const goTo = (path) => {
        setOpen(false);
        setTerm('');
        setQuery('');
        setResults([]);
        navigate(path);
    };

    // The owner's sign-in identity is what /admin/users searches most exactly,
    // so the row lands on that one account rather than on everyone sharing a
    // first name.
    const openOwner = (user) => goTo(`/library?search=${encodeURIComponent(user.email || user.tenantKey || user.displayName || '')}`);

    const handleKeyDown = (event) => {
        if (event.key === 'Escape') {
            setOpen(false);
            return;
        }
        if (event.key === 'Enter' && term.trim()) {
            goTo(`/library?search=${encodeURIComponent(term.trim())}`);
        }
    };

    const showDropdown = open && term.trim().length > 0;

    return (
        <header className="sticky top-0 z-30 border-b border-gray-200/80 bg-white/95 px-4 py-3 backdrop-blur-xl sm:px-6">
          <div className="flex flex-wrap items-center gap-3 lg:flex-nowrap">
            <button type="button" onClick={onMenuClick} aria-label="Open navigation" className="flex h-11 w-11 flex-none items-center justify-center rounded-xl border border-gray-200 text-indigo-950 hover:bg-gray-50 lg:hidden">
                <Menu className="h-5 w-5" />
            </button>
            <div className="min-w-0 flex-1 lg:max-w-[260px]">
                <div className="flex items-center gap-2">
                    <h2 className="truncate text-base font-bold text-indigo-950">{routeTitle[0]}</h2>
                    {isSuperAdmin && <span className="hidden items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-[10px] font-bold uppercase tracking-wide text-emerald-700 sm:inline-flex"><ShieldCheck className="h-3 w-3" />Super admin</span>}
                </div>
                <p className="hidden truncate text-xs text-gray-500 sm:block">{routeTitle[1]}</p>
            </div>

            <div ref={containerRef} className="relative order-3 w-full lg:order-none lg:min-w-[300px] lg:max-w-xl lg:flex-1">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    value={term}
                    onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
                    onFocus={() => setOpen(true)}
                    onKeyDown={handleKeyDown}
                    placeholder="Search users, receipts, plans..."
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 py-2.5 pl-9 pr-3 text-sm focus:bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
                />

                {showDropdown && (
                    <div className="absolute z-40 mt-2 w-full bg-white border border-gray-200 rounded-xl shadow-xl overflow-hidden">
                        {term.trim().length < MIN_QUERY ? (
                            <p className="px-4 py-3 text-sm text-gray-500">Type at least {MIN_QUERY} characters.</p>
                        ) : (
                            <>
                                {searching && (
                                    <p className="px-4 py-3 text-sm text-gray-500 flex items-center gap-2">
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Searching owner accounts...
                                    </p>
                                )}

                                {!searching && searchError && (
                                    <p className="px-4 py-3 text-sm text-red-600">{searchError}</p>
                                )}

                                {!searching && !searchError && results.length === 0 && (
                                    <p className="px-4 py-3 text-sm text-gray-500">
                                        No owner account matches "{query}".
                                    </p>
                                )}

                                {!searching && !searchError && results.length > 0 && (
                                    <ul className="max-h-80 overflow-y-auto divide-y divide-gray-100">
                                        {results.map((user) => (
                                            <li key={user._id}>
                                                <button
                                                    onClick={() => openOwner(user)}
                                                    className="w-full text-left px-4 py-3 hover:bg-gray-50 flex items-start gap-3"
                                                >
                                                    <Building2 className="w-4 h-4 text-gray-400 mt-0.5 flex-shrink-0" />
                                                    <span className="min-w-0 flex-1">
                                                        <span className="block text-sm font-medium text-gray-900 truncate">
                                                            {user.libraryName || user.displayName || 'Unnamed owner'}
                                                        </span>
                                                        <span className="block text-xs text-gray-500 truncate">
                                                            {user.displayName} · {user.email || user.authPhone || user.tenantKey}
                                                        </span>
                                                    </span>
                                                    <span
                                                        className={`text-xs font-medium flex-shrink-0 ${
                                                            user.PaymentStatus ? 'text-green-600' : 'text-gray-400'
                                                        }`}
                                                    >
                                                        {user.PaymentStatus ? 'Active' : 'Inactive'}
                                                    </span>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}

                                {/* Only owner accounts are searchable in one call. Receipts and
                                    plans each have their own screen, so the box hands the term
                                    to them rather than pretending to search everything. */}
                                <div className="border-t border-gray-100 bg-gray-50">
                                    <button
                                        onClick={() => goTo(`/library?search=${encodeURIComponent(term.trim())}`)}
                                        className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                    >
                                        <ArrowRight className="w-4 h-4 text-gray-400" />
                                        See every owner matching "{term.trim()}"
                                    </button>
                                    {isSuperAdmin && (
                                        <button
                                            onClick={() => goTo(`/payment-receipts?search=${encodeURIComponent(term.trim())}`)}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                        >
                                            <Receipt className="w-4 h-4 text-gray-400" />
                                            Search payment receipts
                                        </button>
                                    )}
                                    {isSuperAdmin && (
                                        <button
                                            onClick={() => goTo('/payments?tab=plans')}
                                            className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-100 flex items-center gap-2"
                                        >
                                            <CreditCard className="w-4 h-4 text-gray-400" />
                                            Open subscription plans
                                        </button>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                )}
            </div>

            {dateRange && (
                <div className="flex flex-none items-center gap-2 rounded-xl border border-gray-200 bg-white p-1.5">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <select
                        value={dateRange.preset}
                        onChange={(e) => dateRange.setPreset(e.target.value)}
                        className="border-0 bg-transparent px-2 py-1.5 text-sm font-medium text-gray-700 focus:outline-none focus:ring-0"
                    >
                        {RANGE_PRESETS.map((option) => (
                            <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                        <option value="custom">Custom range</option>
                    </select>

                    <input
                        type="date"
                        value={dateRange.startDate}
                        max={dateRange.endDate}
                        onChange={(e) => dateRange.setCustomRange(e.target.value, dateRange.endDate)}
                        className="hidden border-0 bg-gray-50 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 xl:block"
                    />
                    <span className="hidden text-sm text-gray-400 xl:inline">to</span>
                    <input
                        type="date"
                        value={dateRange.endDate}
                        min={dateRange.startDate}
                        onChange={(e) => dateRange.setCustomRange(dateRange.startDate, e.target.value)}
                        className="hidden border-0 bg-gray-50 px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 xl:block"
                    />
                </div>
            )}
          </div>
        </header>
    );
};

export default TopBar;
